/**
 * Online Incident Management — shared contract (API <-> web <-> worker).
 * Proposal: "Online Incident Management – Oct 2026".
 *
 * The API is authoritative for every rule here. The web uses
 * `availableActions` returned by the API (computed with
 * `incidentAvailableActions`) to decide which buttons to show; hiding a
 * button is never the authorisation.
 *
 * Lifecycle (incident_cases.status; moves also enforced by a DB trigger):
 *
 *   NEW ──triage (categorise + assign)──▶ IN_PROGRESS ──stakeholder submits RCA/CAPA──▶ SUBMITTED
 *   SUBMITTED ──accept──▶ CLOSED
 *   SUBMITTED ──reject (remark) / re-assign (remark)──▶ IN_PROGRESS   (new cycle)
 *   IN_PROGRESS ──re-assign (remark)──▶ IN_PROGRESS                  (new cycle)
 *   CLOSED ──reopen (remark) + assign──▶ IN_PROGRESS                  (new cycle)
 *
 * `cycleKind` records why the current IN_PROGRESS cycle started
 * (INITIAL / REJECTED / REASSIGNED / REOPENED); it drives the resolution SLA
 * and the "Reopened / Rejected / Re-assigned" badges.
 */
import { z } from 'zod';
import { PERMISSIONS } from './permissions.js';
export const INCIDENT_CASE_STATUSES = ['NEW', 'IN_PROGRESS', 'SUBMITTED', 'CLOSED'];
export const INCIDENT_CYCLE_KINDS = ['INITIAL', 'REJECTED', 'REASSIGNED', 'REOPENED'];
export const INCIDENT_CLASSIFICATIONS = ['CLINICAL', 'NON_CLINICAL'];
export const INCIDENT_TYPES = ['SENTINEL', 'ADVERSE', 'NEAR_MISS', 'NO_HARM'];
export const INCIDENT_URGENCIES = ['HIGH', 'MEDIUM', 'LOW'];
export const INCIDENT_CAPA_STATUSES = ['OPEN', 'IN_PROGRESS', 'COMPLETED'];
export const INCIDENT_REVIEW_DECISIONS = ['ACCEPTED', 'REJECTED', 'REASSIGNED', 'REOPENED'];
export const INCIDENT_SLA_STATES = ['PENDING', 'ON_TRACK', 'DUE_SOON', 'OVERDUE', 'MET', 'MISSED'];
/** List / dashboard / export filter on the CURRENT clock of open incidents. */
export const INCIDENT_SLA_FILTERS = ['ON_TRACK', 'DUE_SOON', 'OVERDUE'];
export const INCIDENT_ACTIONS = [
    'TRIAGE',
    'UPDATE_CATEGORIZATION',
    'ADD_STAKEHOLDERS',
    'EDIT_RESPONSE',
    'SUBMIT_RESPONSE',
    'ACCEPT',
    'REJECT',
    'REASSIGN',
    'REOPEN',
];
export const INCIDENT_CASE_STATUS_LABELS = {
    NEW: 'New',
    IN_PROGRESS: 'In progress',
    SUBMITTED: 'Submitted',
    CLOSED: 'Closed',
};
export const INCIDENT_CYCLE_LABELS = {
    INITIAL: 'Assigned',
    REJECTED: 'Rejected',
    REASSIGNED: 'Re-assigned',
    REOPENED: 'Reopened',
};
export const INCIDENT_CLASSIFICATION_LABELS = {
    CLINICAL: 'Clinical',
    NON_CLINICAL: 'Non-clinical',
};
export const INCIDENT_TYPE_LABELS = {
    SENTINEL: 'Sentinel',
    ADVERSE: 'Adverse',
    NEAR_MISS: 'Near Miss',
    NO_HARM: 'No Harm',
};
export const INCIDENT_URGENCY_LABELS = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' };
export const INCIDENT_CAPA_STATUS_LABELS = {
    OPEN: 'Open',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
};
export const INCIDENT_REVIEW_DECISION_LABELS = {
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',
    REASSIGNED: 'Re-assigned',
    REOPENED: 'Reopened',
};
export const INCIDENT_SLA_STATE_LABELS = {
    PENDING: 'Pending',
    ON_TRACK: 'On track',
    DUE_SOON: 'Due soon',
    OVERDUE: 'Overdue',
    MET: 'Met',
    MISSED: 'Missed',
};
export const INCIDENT_WORKFLOW_LIMITS = {
    reasonMin: 3,
    reason: 2000,
    responseText: 5000,
    maxStakeholders: 20,
    workgroupName: 120,
    floorName: 60,
};
/**
 * The proposal's values. The database defaults (incident_sla_settings) carry
 * the same numbers; administrators change them in Admin → Incident management.
 */
export const INCIDENT_SLA_DEFAULTS = {
    responseHours: 24,
    urgencyHighHours: 48,
    urgencyMediumHours: 72,
    urgencyLowHours: 72,
    clinicalHours: 48,
    nonClinicalHours: 72,
    reworkHours: 24,
    rejectedHours: 24,
    warningPercent: 80,
    escalationL2AfterHours: 24,
    escalationL3AfterHours: 48,
};
/**
 * Resolution target (working hours) for the current cycle.
 *
 *  - REASSIGNED / REOPENED: `reworkHours` (proposal: 24).
 *  - REJECTED: `rejectedHours` (the proposal gives no figure; see report).
 *  - INITIAL: the proposal lists an urgency-based AND a classification-based
 *    target without saying which wins when both apply (e.g. Clinical + Low =
 *    48 vs 72). The STRICTER (shorter) target is used so neither commitment
 *    can be missed silently.
 */
export function incidentResolutionTargetHours(settings, input) {
    if (input.cycleKind === 'REASSIGNED' || input.cycleKind === 'REOPENED')
        return settings.reworkHours;
    if (input.cycleKind === 'REJECTED')
        return settings.rejectedHours;
    const byUrgency = input.urgency === 'HIGH' ? settings.urgencyHighHours : input.urgency === 'MEDIUM' ? settings.urgencyMediumHours : settings.urgencyLowHours;
    const byClassification = input.classification === 'CLINICAL' ? settings.clinicalHours : settings.nonClinicalHours;
    return Math.min(byUrgency, byClassification);
}
/** Working minutes after the clock start at which an open clock becomes "due soon". */
export function incidentWarningMinutes(targetHours, warningPercent) {
    return Math.floor((targetHours * 60 * warningPercent) / 100);
}
/**
 * The clock the UI shows. Pure: all the business-time maths happened when the
 * due / warn instants were stored, so only comparisons remain.
 */
export function incidentSlaView(c, now) {
    if (c.status === 'NEW')
        return { phase: 'RESPONSE', state: openState(now, c.responseDueAt, c.responseWarnAt), dueAt: c.responseDueAt };
    if (c.status === 'IN_PROGRESS') {
        return { phase: 'RESOLUTION', state: openState(now, c.resolutionDueAt, c.resolutionWarnAt), dueAt: c.resolutionDueAt };
    }
    // SUBMITTED / CLOSED: the stakeholder clock stopped at submission.
    if (!c.resolutionDueAt || !c.submittedAt)
        return { phase: 'RESOLUTION', state: 'PENDING', dueAt: c.resolutionDueAt };
    return {
        phase: 'RESOLUTION',
        state: c.submittedAt.getTime() <= c.resolutionDueAt.getTime() ? 'MET' : 'MISSED',
        dueAt: c.resolutionDueAt,
    };
}
function openState(now, dueAt, warnAt) {
    if (!dueAt)
        return 'PENDING';
    if (now.getTime() >= dueAt.getTime())
        return 'OVERDUE';
    if (warnAt && now.getTime() >= warnAt.getTime())
        return 'DUE_SOON';
    return 'ON_TRACK';
}
export const INCIDENT_REVIEWER_ROLES = ['QUALITY', 'REPORTER'];
export function incidentReviewCapacity(ctx, action) {
    const quality = action === 'REOPEN' ? PERMISSIONS.INCIDENT_REOPEN : PERMISSIONS.INCIDENT_REVIEW;
    if (ctx.permissions.has(quality))
        return 'QUALITY';
    if (ctx.isReporter && ctx.permissions.has(PERMISSIONS.INCIDENT_CONFIRM_OWN))
        return 'REPORTER';
    return null;
}
export function incidentAvailableActions(ctx) {
    const has = (p) => ctx.permissions.has(p);
    const out = [];
    switch (ctx.status) {
        case 'NEW':
            if (has(PERMISSIONS.INCIDENT_TRIAGE))
                out.push('TRIAGE');
            break;
        case 'IN_PROGRESS':
            if (has(PERMISSIONS.INCIDENT_TRIAGE))
                out.push('UPDATE_CATEGORIZATION', 'ADD_STAKEHOLDERS');
            if (ctx.isActiveStakeholder && has(PERMISSIONS.INCIDENT_RESPOND))
                out.push('EDIT_RESPONSE', 'SUBMIT_RESPONSE');
            if (has(PERMISSIONS.INCIDENT_REVIEW))
                out.push('REASSIGN');
            break;
        case 'SUBMITTED': {
            if (has(PERMISSIONS.INCIDENT_TRIAGE))
                out.push('UPDATE_CATEGORIZATION');
            const reviewer = incidentReviewCapacity(ctx, 'ACCEPT');
            if (reviewer) {
                // Separation of duties: nobody signs off work they did themselves.
                if (!ctx.isSubmitter && !ctx.isActiveStakeholder)
                    out.push('ACCEPT');
                out.push('REJECT');
                if (reviewer === 'QUALITY')
                    out.push('REASSIGN');
            }
            break;
        }
        case 'CLOSED':
            if (incidentReviewCapacity(ctx, 'REOPEN'))
                out.push('REOPEN');
            break;
    }
    return out;
}
// ---------------------------------------------------------------------------
// Request DTOs
// ---------------------------------------------------------------------------
const uuid = () => z.string().uuid();
const stakeholders = () => z
    .array(uuid())
    .min(1, 'Choose at least one stakeholder.')
    .max(INCIDENT_WORKFLOW_LIMITS.maxStakeholders)
    .transform((a) => [...new Set(a)]);
/** A required remark: trimmed, at least 3 characters. */
export const incidentReasonSchema = z
    .string()
    .trim()
    .min(INCIDENT_WORKFLOW_LIMITS.reasonMin, 'A remark is required.')
    .max(INCIDENT_WORKFLOW_LIMITS.reason);
const categorizationShape = {
    classification: z.enum(INCIDENT_CLASSIFICATIONS),
    incidentType: z.enum(INCIDENT_TYPES),
    urgency: z.enum(INCIDENT_URGENCIES),
    workgroupId: uuid(),
};
/** `POST /incidents/:id/triage` — NEW -> IN_PROGRESS. */
export const incidentTriageDtoSchema = z.object({ ...categorizationShape, stakeholderIds: stakeholders() }).strict();
/** `PATCH /incidents/:id/categorization` — correct the categorisation (IN_PROGRESS / SUBMITTED). */
export const incidentCategorizationDtoSchema = z
    .object(categorizationShape)
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field is required' });
/** `POST /incidents/:id/stakeholders` — add stakeholders to the current cycle (no clock change). */
export const incidentAddStakeholdersDtoSchema = z.object({ stakeholderIds: stakeholders() }).strict();
const responseText = () => z
    .string()
    .trim()
    .max(INCIDENT_WORKFLOW_LIMITS.responseText)
    .transform((v) => (v.length === 0 ? null : v))
    .nullable();
const isoDate = () => z.union([
    z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.')
        .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'Not a valid date.'),
    z.literal('').transform(() => null),
    z.null(),
]);
/** `PATCH /incidents/:id/response` — save RCA / CAPA (active stakeholder, IN_PROGRESS). */
export const incidentResponseDtoSchema = z
    .object({
    rcaRootCause: responseText(),
    rcaContributingFactors: responseText(),
    rcaInvestigationNotes: responseText(),
    rcaFindings: responseText(),
    capaCorrectiveAction: responseText(),
    capaPreventiveAction: responseText(),
    capaOwnerId: z.union([uuid(), z.literal('').transform(() => null), z.null()]),
    capaTargetDate: isoDate(),
    capaCompletedDate: isoDate(),
    capaStatus: z.enum(INCIDENT_CAPA_STATUSES).nullable(),
    capaComments: responseText(),
})
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field is required' });
/** Fields a stakeholder must complete before submitting RCA/CAPA (server-enforced). */
export const INCIDENT_RESPONSE_REQUIRED = ['rcaRootCause', 'capaCorrectiveAction', 'capaPreventiveAction'];
export function findMissingResponseFields(r) {
    return INCIDENT_RESPONSE_REQUIRED.filter((f) => !r[f] || r[f].trim().length === 0);
}
/** `POST /incidents/:id/review` — Quality decision on a SUBMITTED RCA/CAPA. */
export const incidentReviewDtoSchema = z.discriminatedUnion('decision', [
    z.object({ decision: z.literal('ACCEPT'), reason: z.string().trim().max(INCIDENT_WORKFLOW_LIMITS.reason).optional() }).strict(),
    z.object({ decision: z.literal('REJECT'), reason: incidentReasonSchema }).strict(),
    z
        .object({ decision: z.literal('REASSIGN'), reason: incidentReasonSchema, stakeholderIds: stakeholders(), workgroupId: uuid().optional() })
        .strict(),
]);
/** `POST /incidents/:id/reassign` — Quality re-assigns an IN_PROGRESS incident. */
export const incidentReassignDtoSchema = z
    .object({ reason: incidentReasonSchema, stakeholderIds: stakeholders(), workgroupId: uuid().optional() })
    .strict();
/**
 * `POST /incidents/:id/reopen` — CLOSED -> IN_PROGRESS. Quality may choose new
 * stakeholders / workgroup; without `stakeholderIds` the incident goes back to
 * its last stakeholders (the only option for the reporter).
 */
export const incidentReopenDtoSchema = z
    .object({ reason: incidentReasonSchema, stakeholderIds: stakeholders().optional(), workgroupId: uuid().optional() })
    .strict();
/** `GET /incidents/stakeholder-search?q=` */
export const incidentStakeholderSearchSchema = z.object({
    q: z.string().trim().max(100).default(''),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
// ---------------------------------------------------------------------------
// Admin configuration
// ---------------------------------------------------------------------------
const hours = () => z.number().int().min(1).max(2000);
export const incidentSlaSettingsDtoSchema = z
    .object({
    calendarId: uuid().nullable(),
    responseHours: hours(),
    urgencyHighHours: hours(),
    urgencyMediumHours: hours(),
    urgencyLowHours: hours(),
    clinicalHours: hours(),
    nonClinicalHours: hours(),
    reworkHours: hours(),
    rejectedHours: hours(),
    warningPercent: z.number().int().min(1).max(99),
    escalationL2AfterHours: hours(),
    escalationL3AfterHours: z.number().int().min(2).max(4000),
})
    .strict()
    .refine((v) => v.escalationL3AfterHours > v.escalationL2AfterHours, {
    message: 'Level 3 must come after Level 2',
    path: ['escalationL3AfterHours'],
});
export const incidentWorkgroupWriteSchema = z
    .object({
    name: z.string().trim().min(1).max(INCIDENT_WORKFLOW_LIMITS.workgroupName),
    sortOrder: z.number().int().min(0).max(10000).optional(),
    isActive: z.boolean().optional(),
    memberIds: z.array(uuid()).max(100).transform((a) => [...new Set(a)]).optional(),
})
    .strict();
export const incidentFloorWriteSchema = z
    .object({
    name: z.string().trim().min(1).max(INCIDENT_WORKFLOW_LIMITS.floorName),
    sortOrder: z.number().int().min(0).max(10000).optional(),
    isActive: z.boolean().optional(),
})
    .strict();
export const incidentEscalationRecipientWriteSchema = z
    .object({
    workgroupId: uuid().nullable(),
    level: z.number().int().min(1).max(3),
    userId: uuid().optional(),
    email: z.string().trim().toLowerCase().email().max(254).optional(),
})
    .strict()
    .refine((v) => (v.userId === undefined) !== (v.email === undefined), { message: 'Give either a user or an email address' });
//# sourceMappingURL=incident-workflow.js.map